/**
 * OrgSettingsScreen — district name, contact email, billing email.
 *
 * Read-only in v1 (editing org settings requires service-role
 * writes because org_settings has no per-user RLS). Future work:
 * wire up an `update_org_settings` RPC that's gated by
 * has_org_role(auth.uid(), orgId, 'admin').
 */
import { useEffect, useState } from 'react';
import type { ReactElement } from 'react';
import { supabase } from '../../lib/supabase/client';
import { FormSkeleton } from '../../components/Skeleton';
import { humanError } from '../../lib/ui/errorCopy';

interface OrgInfo {
  id: string;
  name: string;
  state: string | null;
}

interface Props {
  orgId: string;
}

export function OrgSettingsScreen({ orgId }: Props): ReactElement {
  const [org, setOrg] = useState<OrgInfo | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    void (async () => {
      const { data, error: e } = await supabase
        .from('organizations')
        .select('id, name, state')
        .eq('id', orgId)
        .maybeSingle();
      if (cancelled) return;
      if (e) {
        setError(humanError(e));
      } else {
        setOrg((data as OrgInfo | null) ?? null);
      }
      setLoading(false);
    })();
    return () => {
      cancelled = true;
    };
  }, [orgId]);

  if (loading) return <FormSkeleton rows={3} />;
  if (error) {
    return (
      <div className="admin-settings">
        <h2>Settings</h2>
        <section className="empty-state" aria-live="assertive">
          <h2 className="empty-state-title">
            Couldn&rsquo;t load your district settings
          </h2>
          <p className="empty-state-body">{error}</p>
          <button
            type="button"
            className="snap-button empty-state-cta"
            onClick={() => {
              window.location.reload();
            }}
          >
            Try again
          </button>
        </section>
      </div>
    );
  }
  if (!org) {
    return (
      <div className="admin-settings">
        <h2>Settings</h2>
        <section className="empty-state" aria-live="polite">
          <h2 className="empty-state-title">
            Your district isn&rsquo;t set up yet
          </h2>
          <p className="empty-state-body">
            Reach out to support@effectiveslp.app so we can finish
            setting up your district.
          </p>
        </section>
      </div>
    );
  }

  return (
    <div className="admin-settings">
      <h2>Settings</h2>
      <dl className="settings-list">
        <div>
          <dt>District name</dt>
          <dd>{org.name}</dd>
        </div>
        <div>
          <dt>State</dt>
          <dd>{org.state ?? '—'}</dd>
        </div>
        <div>
          <dt>Organization ID</dt>
          <dd>
            <code>{org.id}</code>
          </dd>
        </div>
      </dl>
      <p className="tagline">
        Editing org settings from the web console is a follow-up
        ticket. Reach out to support@effectiveslp.app to update
        name, state, or billing email.
      </p>
    </div>
  );
}